import React, { useEffect, useState, useRef } from 'react';
import api from '../api/client';
import logo from '../logo.png';

const SLIDE_DURATION = 6000;
const FADE_DURATION  = 600;

export default function SponsorShow() {
  const [sponsors, setSponsors] = useState([]);
  const [idx, setIdx]           = useState(0);
  const [visible, setVisible]   = useState(true);
  const [progress, setProgress] = useState(0);
  const timerRef    = useRef(null);
  const progRef     = useRef(null);
  const progStart   = useRef(null);

  useEffect(() => {
    api.get('/api/sponsors').then((r) => setSponsors(r.data)).catch(() => {});
  }, []);

  const goTo = (next) => {
    setVisible(false);
    setTimeout(() => {
      setIdx(next);
      setVisible(true);
      setProgress(0);
      progStart.current = Date.now();
    }, FADE_DURATION);
  };

  useEffect(() => {
    if (sponsors.length <= 1) return;
    timerRef.current = setTimeout(() => {
      goTo((idx + 1) % sponsors.length);
    }, SLIDE_DURATION);
    return () => clearTimeout(timerRef.current);
  }, [idx, sponsors.length]); // eslint-disable-line

  // Smooth progress bar
  useEffect(() => {
    if (sponsors.length <= 1) return;
    progStart.current = Date.now();
    const tick = () => {
      const elapsed = Date.now() - progStart.current;
      setProgress(Math.min(100, (elapsed / SLIDE_DURATION) * 100));
      progRef.current = requestAnimationFrame(tick);
    };
    progRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(progRef.current);
  }, [idx, sponsors.length]);

  const s = sponsors[idx];

  return (
    <div
      className="fixed inset-0 bg-ocean-950 flex flex-col overflow-hidden select-none"
      style={{ fontFamily: 'inherit' }}
    >
      {/* Logo oben links */}
      <div className="flex items-center px-8 pt-6 pb-2 z-20" style={{ minHeight: 100 }}>
        <img src={logo} alt="BeachLAN" className="h-40 w-auto object-contain drop-shadow-lg opacity-90 flex-shrink-0" />
        <div className="flex-1 flex justify-center pr-40">
          <h1 className="text-sand-300 font-black uppercase tracking-widest drop-shadow-lg" style={{ fontSize: 'clamp(2rem, 5vw, 4rem)' }}>
            Sponsoren
          </h1>
        </div>
      </div>

      {/* Haupt-Inhalt */}
      <div
        className="flex-1 flex flex-col items-center justify-center px-12"
        style={{
          opacity: visible ? 1 : 0,
          transition: `opacity ${FADE_DURATION}ms ease`,
        }}
      >
        {!s ? (
          <p className="text-white/30 text-xl tracking-widest uppercase">Keine Sponsoren</p>
        ) : s.logo_path ? (
          <>
            <div
              className="flex items-center justify-center rounded-2xl mb-10"
              style={{
                background: '#fff',
                padding: '48px 80px',
                minWidth: 480,
                minHeight: 300,
                boxShadow: '0 0 120px rgba(255,255,255,0.12)',
              }}
            >
              <img
                src={s.logo_path}
                alt={s.name}
                style={{ maxHeight: 280, maxWidth: 800, objectFit: 'contain', display: 'block' }}
              />
            </div>
            <p className="text-sand-300 text-4xl font-black tracking-wide mb-3 text-center drop-shadow-lg">
              {s.name}
            </p>
          </>
        ) : (
          <p
            className="text-white font-black text-center drop-shadow-2xl"
            style={{ fontSize: 'clamp(3rem, 10vw, 9rem)', lineHeight: 1.05, letterSpacing: '-0.02em' }}
          >
            {s.name}
          </p>
        )}

        {s?.website && (
          <p className="text-ocean-300 text-lg mt-2 tracking-wide opacity-80">
            {s.website.replace(/^https?:\/\//, '')}
          </p>
        )}
      </div>

      {/* Fortschrittsbalken */}
      {sponsors.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
          <div
            className="h-full bg-sand-400"
            style={{ width: `${progress}%`, transition: 'width 0.1s linear' }}
          />
        </div>
      )}

      {/* Punkte-Navigation */}
      {sponsors.length > 1 && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2.5 z-20">
          {sponsors.map((_, i) => (
            <button
              key={i}
              onClick={() => { clearTimeout(timerRef.current); goTo(i); }}
              className="border-0 cursor-pointer rounded-full transition-all duration-300"
              style={{
                width: i === idx ? 24 : 8,
                height: 8,
                background: i === idx ? '#F5C842' : 'rgba(255,255,255,0.25)',
              }}
            />
          ))}
        </div>
      )}

      {/* Gesamtanzahl rechts unten */}
      {sponsors.length > 1 && (
        <div className="absolute bottom-6 right-8 text-white/25 text-sm font-medium tabular-nums">
          {idx + 1} / {sponsors.length}
        </div>
      )}
    </div>
  );
}
